import { computed, ref } from 'vue';

import type { ComputedRef, Ref } from 'vue';

import { useInternationalization } from '@/i18n';
import {
  type LanguageOption,
  LANGUAGES,
  type SupportedLanguage
} from '@/i18n/constants';

const language = ref<SupportedLanguage | null>(null);
const isLanguageDialogOpen = ref(false);

interface UseLanguageSelector {
  supportedLanguages: readonly LanguageOption[];
  language: Ref<SupportedLanguage | null>;
  isLanguageDialogOpen: Ref<boolean>;
  formattedSelectedLanguage: ComputedRef<string>;
  // This composable is the UI-selector wrapper: callers iterate `supportedLanguages`
  // (typed LanguageOption) and pass one of its `code` values back. The underlying
  // setLanguage still validates at runtime for code paths that bypass the type system.
  selectLanguage: (newLocale: SupportedLanguage) => Promise<void>;
  setLanguageDialogOpen: (isOpen: boolean) => void;
}

export const useLanguageSelector = (): UseLanguageSelector => {
  const { currentLanguage, setLanguage } = useInternationalization();

  if (!language.value) {
    language.value = currentLanguage.value;
  }

  const formattedSelectedLanguage = computed(() => {
    const found = LANGUAGES.find((lang) => lang.code === language.value);
    return found ? found.display.toUpperCase() : 'EN';
  });

  const selectLanguage = async (
    newLocale: SupportedLanguage
  ): Promise<void> => {
    try {
      await setLanguage(newLocale);
      // Sync UI selection from the source of truth after setLanguage completes.
      // setLanguage can normalize inputs (e.g. "nl" → "nl_NL") or return early for unsupported locales.
      language.value = currentLanguage.value;
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  const setLanguageDialogOpen = (isOpen: boolean): void => {
    isLanguageDialogOpen.value = isOpen;
  };

  return {
    supportedLanguages: LANGUAGES,
    language,
    isLanguageDialogOpen,
    formattedSelectedLanguage,
    selectLanguage,
    setLanguageDialogOpen
  };
};
